import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Users, Phone, Mail, MapPin, Building, Plus, Edit, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';

const KeyPersonnel = () => {
  const { user } = useAuth();
  const [personnel, setPersonnel] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPersonnel, setExpandedPersonnel] = useState({});
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    designation: '',
    role: 'FieldOfficer',
    email: '',
    phone: '',
    department: '',
    responsibility: '',
    password: 'goa123' // Default password
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, areasRes] = await Promise.all([
        api.users.getAll().catch(() => ({ data: [] })),
        api.areas.getAll()
      ]);
      
      setPersonnel(usersRes.data || []);
      setAreas(areasRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPersonnel = async () => {
    try {
      await api.auth.register(formData);
      setShowAddModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to add personnel:', error);
      alert('Failed to add personnel: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditPersonnel = (person) => {
    setSelectedPerson(person);
    setFormData({
      name: person.name,
      designation: person.designation || '',
      role: person.role,
      email: person.email,
      phone: person.phone,
      department: person.department || '',
      responsibility: person.responsibility || '',
      password: '' // Don't pre-fill password
    });
    setShowEditModal(true);
  };

  const handleUpdatePersonnel = async () => {
    try {
      // In a real app, you'd have a PUT endpoint for users
      alert('Update functionality would call PUT /api/users/' + selectedPerson.id);
      setShowEditModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to update personnel:', error);
      alert('Failed to update personnel');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      designation: '',
      role: 'FieldOfficer',
      email: '',
      phone: '',
      department: '',
      responsibility: '',
      password: 'goa123'
    });
  };

  const canManage = () => {
    return user && ['Minister', 'DeptHead'].includes(user.role);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'Minister': return 'bg-purple-500';
      case 'DeptHead': return 'bg-blue-500';
      case 'FieldOfficer': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const talukas = areas.filter(a => a.type === 'Taluka');

  const togglePersonnel = (id) => {
    setExpandedPersonnel(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleAllPersonnel = () => {
    const allExpanded = Object.keys(expandedPersonnel).length === personnel.length && 
                        Object.values(expandedPersonnel).every(v => v);
    
    if (allExpanded) {
      setExpandedPersonnel({});
    } else {
      const expanded = {};
      personnel.forEach((p, idx) => {
        expanded[idx] = true;
      });
      setExpandedPersonnel(expanded);
    }
  };

  return (
    <div className="space-y-6">
      {/* Taluka Contacts */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building size={20} />
            Taluka Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {talukas.map((taluka) => (
              <div key={taluka.id} className="bg-slate-800 rounded-lg p-6 border border-slate-700">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">{taluka.name} Taluka</h3>
                    <p className="text-sm text-gray-400">Population: {taluka.population.toLocaleString()}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <Building size={24} className="text-white" />
                  </div>
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-center gap-3">
                    <Users size={16} className="text-gray-400" />
                    <span className="text-gray-300">{taluka.taluka_contact}</span>
                  </div>
                  {taluka.taluka_email && (
                    <div className="flex items-center gap-3">
                      <Mail size={16} className="text-gray-400" />
                      <a href={`mailto:${taluka.taluka_email}`} className="text-blue-400 hover:text-blue-300">
                        {taluka.taluka_email}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <MapPin size={16} className="text-gray-400" />
                    <span className="text-gray-300">
                      {taluka.latitude.toFixed(4)}°N, {taluka.longitude.toFixed(4)}°E
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Key Officials */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle className="flex items-center gap-2">
                <Users size={20} />
                Key Officials - South Goa Water Management ({personnel.length})
              </CardTitle>
              <Button 
                size="sm" 
                variant="outline"
                onClick={toggleAllPersonnel}
                className="text-white border-slate-600 hover:bg-slate-700"
              >
                {Object.keys(expandedPersonnel).length === personnel.length && Object.values(expandedPersonnel).every(v => v) ? (
                  <>
                    <ChevronUp size={16} className="mr-1" />
                    Collapse All
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} className="mr-1" />
                    Expand All
                  </>
                )}
              </Button>
            </div>
            {canManage() && (
              <Button onClick={() => setShowAddModal(true)} className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30">
                <Plus size={16} className="mr-2" />
                Add Personnel
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : (
            <div className="space-y-3">
              {personnel.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No personnel found</p>
              ) : (
                personnel.map((person, idx) => (
                  <div
                    key={idx}
                    className="bg-slate-800 rounded-lg border border-slate-700 hover:border-slate-600 transition-colors"
                  >
                    {/* Collapsed Header */}
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer"
                      onClick={() => togglePersonnel(idx)}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`w-12 h-12 ${getRoleColor(person.role)} rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
                          {(person.designation || person.name).split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-base font-bold text-white">{person.designation || person.name}</h3>
                          <p className="text-xs text-blue-400">{person.department || person.role}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getRoleColor(person.role)} text-white`}>
                            {person.role}
                          </span>
                          {expandedPersonnel[idx] ? (
                            <ChevronUp size={20} className="text-gray-400" />
                          ) : (
                            <ChevronDown size={20} className="text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedPersonnel[idx] && (
                      <div className="px-4 pb-4 border-t border-slate-700">
                        <div className="mt-4 space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm">
                                <Phone size={16} className="text-gray-400" />
                                <a href={`tel:${person.phone}`} className="text-gray-300 hover:text-white">
                                  {person.phone}
                                </a>
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <Mail size={16} className="text-gray-400" />
                                <a href={`mailto:${person.email}`} className="text-gray-300 hover:text-white truncate">
                                  {person.email}
                                </a>
                              </div>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div>
                                <p className="text-xs text-gray-400">Department</p>
                                <p className="text-gray-300">{person.department || 'N/A'}</p>
                              </div>
                            </div>
                          </div>
                          
                          {person.responsibility && (
                            <div className="pt-3 border-t border-slate-700">
                              <p className="text-xs text-gray-400 mb-1">Responsibility</p>
                              <p className="text-sm text-gray-300">{person.responsibility}</p>
                            </div>
                          )}

                          {canManage() && (
                            <div className="pt-3 border-t border-slate-700 flex gap-2">
                              <Button 
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditPersonnel(person);
                                }}
                                className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30"
                              >
                                <Edit size={14} className="mr-1" />
                                Edit Personnel
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Personnel Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Personnel</DialogTitle>
            <DialogDescription>
              Register a new official in the water management system.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="name">Functional Designation *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Chief Engineer, Executive Engineer - Salcette"
              />
              <p className="text-xs text-gray-500">Enter designation only, not personal name</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+91-XXX-XXXXXXX"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role *</Label>
              <Select 
                value={formData.role}
                onValueChange={(value) => setFormData({...formData, role: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Minister">Minister</SelectItem>
                  <SelectItem value="DeptHead">Department Head</SelectItem>
                  <SelectItem value="FieldOfficer">Field Officer</SelectItem>
                  <SelectItem value="Public">Public</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="designation">Designation</Label>
              <Input
                id="designation"
                value={formData.designation}
                onChange={(e) => setFormData({...formData, designation: e.target.value})}
                placeholder="e.g., Junior Engineer"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="department">Department</Label>
              <Input
                id="department"
                value={formData.department}
                onChange={(e) => setFormData({...formData, department: e.target.value})}
                placeholder="e.g., Margao Sub-Division"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="responsibility">Responsibility</Label>
              <Textarea
                id="responsibility"
                value={formData.responsibility}
                onChange={(e) => setFormData({...formData, responsibility: e.target.value})}
                placeholder="Describe responsibilities..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddPersonnel} className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30">
              Add Personnel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Personnel Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Personnel</DialogTitle>
            <DialogDescription>
              Update personnel information.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Full Name</Label>
              <Input
                id="edit_name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_phone">Phone</Label>
              <Input
                id="edit_phone"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_designation">Designation</Label>
              <Input
                id="edit_designation"
                value={formData.designation}
                onChange={(e) => setFormData({...formData, designation: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_department">Department</Label>
              <Input
                id="edit_department"
                value={formData.department}
                onChange={(e) => setFormData({...formData, department: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_responsibility">Responsibility</Label>
              <Textarea
                id="edit_responsibility"
                value={formData.responsibility}
                onChange={(e) => setFormData({...formData, responsibility: e.target.value})}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdatePersonnel} className="bg-gradient-to-br from-cyan-600/80 to-cyan-700/80 hover:from-cyan-600 hover:to-cyan-700 border border-cyan-500/50 transition-all hover:shadow-lg hover:shadow-cyan-500/30">
              Update Personnel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emergency Contacts */}
      <Card className="bg-[#12172e] border-cyan-500/30 hover:border-cyan-500/50 transition-all text-white">
        <CardHeader>
          <CardTitle>Emergency Contacts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-1">Control Room</p>
              <p className="text-xl font-bold text-red-400">+91-832-2420000</p>
            </div>
            <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-1">Water Quality Lab</p>
              <p className="text-xl font-bold text-orange-400">+91-832-2420100</p>
            </div>
            <div className="bg-blue-500/20 border border-blue-500 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-1">Maintenance Helpline</p>
              <p className="text-xl font-bold text-blue-400">+91-832-2420200</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default KeyPersonnel;
